// =============================================================================
// src/features/explore/explore-styling.ts — Coloracao/estilo/legenda (puro).
//
// Proveniencia: assets/explore.js `munColor`/`munDimmed`/`munStyle` (274-290),
//   `zoneColor`/`zoneDimmed`/`zoneStyle` (292-309), `getLegend` (378-394) e
//   `densBucket` (145-148). Extraidos do controller como FUNCOES PURAS que recebem
//   um snapshot do estado (ExploreStyleState) — mantem o arquivo do mapa < 500
//   linhas e permite testar a coloracao sem Leaflet. Cores/opacidades/limiares sao
//   paridade visual — NAO alterar.
//
// SRP: so calculo de cor/estilo/legenda. Sem DOM, sem Leaflet, sem mutacao.
// =============================================================================

import type { PathOptions } from 'leaflet';
import type {
  ZonaKey,
  MunicipalityId,
  HexColor,
  AmazonasRegion,
  ElectoralYear,
  ExploreThematicAxis,
  ExploreElectionResult,
} from '../../app/types';
import {
  EXPLORE_DATASET,
  MANAUS_ELECTORAL_BY_YEAR,
  MANAUS_LAYER_COLORS,
  campaignProfileOf,
} from '../../data';
import {
  PARTY_COLORS,
  PARTY_ORDER,
  RAMPS,
  METRIC_META,
  BUCKET_LABELS,
  ZONE_THEME,
  ALIGN_COLORS,
  ALIGN_LEGEND,
  ELECTION_OFFICE,
  OFFICE_LABEL,
  type ExploreColorMetric,
} from './constants';
import {
  thematicLevelOf,
  eleitoradoBandOf,
  volatilidadeOf,
  variacao2422Of,
  campoBatalhaOf,
} from './explore-helpers';
import { g26Apuracao, g26LeaderParty, g26RunnerUpParty, g26LeaderPct, g26RegionColor, g26PartiesPresent, type G26Selection } from './explore-g26-derive';
import { g26PartyColor, G26_CARGO_SHORT } from './explore-g26-constants';

/** Snapshot do estado do mapa que afeta cor/estilo/legenda (sem o objeto Leaflet). */
export interface ExploreStyleState {
  readonly colorBy: ExploreColorMetric;
  readonly year: ElectoralYear;
  readonly party: string | null;
  readonly region: AmazonasRegion | null;
  /** Recorte por microrregiao (esmaece os demais). */
  readonly micro: string | null;
  /** Recorte por faixa de eleitorado (pareado com eleitoradoBandOf). */
  readonly eleitoradoBand: string | null;
  /** Recorte por 2º colocado (esmaece quem nao tem esse partido como vice). */
  readonly runnerUp: string | null;
  /** Thresholds de densidade por quartil [q25,q50,q80]. */
  readonly densThresholds: readonly [number, number, number];
  /** Thresholds de populacao (habK) por quartil [q25,q50,q80] (metrica 'populacao'). */
  readonly popThresholds: readonly [number, number, number];
  /**
   * Modo Eleicoes Gerais 2026 (TSE oficial) — quando presente, partido/2º/
   * competitividade e os recortes de partido/2º leem 2026 em vez de `el[year]`.
   */
  readonly g26?: G26Selection | null;
}

/** Bloco de legenda: titulo + pares [rotulo, cor]. */
export interface ExploreLegend {
  readonly title: string;
  readonly items: readonly (readonly [label: string, color: HexColor])[];
}

/** Cor neutra de fallback (paridade: '#D9D6CE'). */
const FALLBACK_COLOR: HexColor = '#D9D6CE';

/** Balde de densidade 0..3 de um valor pelos thresholds (assets/explore.js 145-148). */
export function densBucket(value: number, thresholds: readonly [number, number, number]): number {
  const [t0, t1, t2] = thresholds;
  return value >= t2 ? 3 : value >= t1 ? 2 : value >= t0 ? 1 : 0;
}

/** Cor de uma rampa por nome+balde, com guarda defensiva (rampa real nunca vazia). */
function rampColor(metric: string, bucket: number): HexColor {
  const ramp = RAMPS[metric] ?? RAMPS.educacao ?? [];
  return ramp[bucket] ?? ramp[0] ?? FALLBACK_COLOR;
}

/** Thresholds fixos de IDH (metrica 'idh') — faixas de desenvolvimento humano. */
const IDH_THRESHOLDS: readonly [number, number, number] = [0.50, 0.55, 0.60];
/** Thresholds fixos de competitividade (% do 1º colocado) — disputado -> dominante. */
const COMP_THRESHOLDS: readonly [number, number, number] = [45, 50, 55];
/** Thresholds dos índices estratégicos (decisao de produto 2026-06-01). */
const VOLATILIDADE_THRESHOLDS: readonly [number, number, number] = [3, 4, 5];
const VARIACAO_THRESHOLDS: readonly [number, number, number] = [2, 4, 6];
const CAMPO_BATALHA_THRESHOLDS: readonly [number, number, number] = [0.45, 0.52, 0.58];

/** Balde 0..3 de IDH pelas faixas fixas (reusa densBucket). */
export function idhBucket(idh: number): number {
  return densBucket(idh, IDH_THRESHOLDS);
}

/** Balde 0..3 de competitividade pelo % do 1º colocado (reusa densBucket). */
export function compBucket(pct: number): number {
  return densBucket(pct, COMP_THRESHOLDS);
}

/** Cor do vencedor (ou 2º) de um municipio no ano, com fallback neutro. */
function partyColorOf(result: ExploreElectionResult | undefined, sigla: 'p' | 's'): HexColor {
  return result ? (PARTY_COLORS[result[sigla]] ?? '#B7B3AA') : FALLBACK_COLOR;
}

/** Metricas que, no modo 2026, leem o resultado oficial de 2026. */
const G26_METRICS: ReadonlySet<ExploreColorMetric> = new Set<ExploreColorMetric>(['partido', 'segundo', 'competitividade']);

/** Cor de um municipio no modo 2026 (lider/2º/competitividade do cargo ativo). */
function g26MunColor(id: MunicipalityId, sel: G26Selection, metric: ExploreColorMetric): HexColor {
  if (metric === 'competitividade') {
    const apu = g26Apuracao(sel.ds, 'mun', id, sel.cargo);
    return rampColor('competitividade', compBucket(apu ? g26LeaderPct(sel.ds, apu) : 0));
  }
  return g26RegionColor(sel, id, metric === 'segundo' ? 's' : 'p') ?? FALLBACK_COLOR;
}

/** Cor de preenchimento de um municipio pela metrica ativa (assets/explore.js 274-280). */
function munColor(id: MunicipalityId, state: ExploreStyleState): HexColor {
  const mun = EXPLORE_DATASET.munis[id];
  if (!mun) return FALLBACK_COLOR;
  const metric = state.colorBy;
  /* v8 ignore next -- munPathStyle desvia p/ cleanModeStyle quando colorBy==='nenhum'; munColor nunca recebe 'nenhum' */
  if (metric === 'nenhum') return FALLBACK_COLOR;
  if (state.g26 && G26_METRICS.has(metric)) return g26MunColor(id, state.g26, metric);
  if (metric === 'partido') return partyColorOf(mun.el[state.year], 'p');
  if (metric === 'segundo') return partyColorOf(mun.el[state.year], 's');
  if (metric === 'densidade') return rampColor('densidade', densBucket(mun.eleitoresK ?? 0, state.densThresholds));
  if (metric === 'populacao') return rampColor('populacao', densBucket(mun.habK ?? 0, state.popThresholds));
  if (metric === 'idh') return rampColor('idh', idhBucket(mun.idh));
  if (metric === 'competitividade') return rampColor('competitividade', compBucket(mun.el[state.year]?.pct ?? 0));
  if (metric === 'alinhamento') return ALIGN_COLORS[campaignProfileOf(id).alinhamento];
  if (metric === 'cobertura') return rampColor('cobertura', campaignProfileOf(id).cobertura);
  if (metric === 'reduto') return rampColor('reduto', campaignProfileOf(id).reduto);
  if (metric === 'volatilidade') return rampColor('volatilidade', densBucket(volatilidadeOf(mun), VOLATILIDADE_THRESHOLDS));
  if (metric === 'variacao2422') return rampColor('variacao2422', densBucket(variacao2422Of(mun), VARIACAO_THRESHOLDS));
  if (metric === 'campoBatalha') return rampColor('campoBatalha', densBucket(campoBatalhaOf(mun), CAMPO_BATALHA_THRESHOLDS));
  return rampColor(metric, thematicLevelOf(mun, metric as ExploreThematicAxis));
}

/** Campos de estado que definem o recorte (subset de ExploreStyleState). */
export type ExploreRecorteState = Pick<
  ExploreStyleState,
  'year' | 'region' | 'party' | 'micro' | 'runnerUp' | 'eleitoradoBand' | 'g26'
>;

/** Partido lider (p) e 2º (s) do municipio: 2026 oficial no modo 2026, senao `el[year]`. */
function recortePartiesOf(id: MunicipalityId, state: ExploreRecorteState): { p: string | null; s: string | null } {
  if (state.g26) {
    const apu = g26Apuracao(state.g26.ds, 'mun', id, state.g26.cargo);
    return apu ? { p: g26LeaderParty(state.g26.ds, apu), s: g26RunnerUpParty(state.g26.ds, apu) } : { p: null, s: null };
  }
  const result = EXPLORE_DATASET.munis[id]?.el[state.year];
  return { p: result?.p ?? null, s: result?.s ?? null };
}

/**
 * True quando o municipio PASSA em todos os recortes ativos (regiao, partido
 * vencedor, microrregiao, 2º colocado, faixa de eleitorado) em AND. Municipio sem
 * dado nao e excluido (paridade com o munDimmed original). Exportado para a tela
 * de Comparar reusar exatamente o mesmo predicado de filtro (DRY).
 */
export function munMatchesRecorte(id: MunicipalityId, state: ExploreRecorteState): boolean {
  const mun = EXPLORE_DATASET.munis[id];
  if (!mun) return true;
  const parties = recortePartiesOf(id, state);
  if (state.region && mun.region !== state.region) return false;
  if (state.party && parties.p !== state.party) return false;
  if (state.micro && mun.micro !== state.micro) return false;
  if (state.runnerUp && parties.s !== state.runnerUp) return false;
  if (state.eleitoradoBand && eleitoradoBandOf(mun.eleitoresK) !== state.eleitoradoBand) return false;
  return true;
}

/**
 * True quando o municipio deve ser esmaecido (fora do recorte ativo). Delega a
 * munMatchesRecorte para nao duplicar a regra (comportamento identico ao original).
 * diverge do golden por decisao de produto 2026-05-31 (recortes novos).
 */
function munDimmed(id: MunicipalityId, state: ExploreStyleState): boolean {
  return !munMatchesRecorte(id, state);
}

/** Estilo "limpo": so divisas leves, sem preenchimento; o clique ainda atravessa. */
const CLEAN_PATH: PathOptions = { fillColor: FALLBACK_COLOR, fillOpacity: 0, color: '#9AA3AE', weight: 0.5, opacity: 0.55 };

/** Realce neutro do modo limpo (reusa #1F1F1F, a cor de selecao do hover/clique). */
const CLEAN_SELECTED_PATH: PathOptions = { fillColor: '#1F1F1F', fillOpacity: 0.10, color: '#1F1F1F', weight: 1.2, opacity: 0.9 };
/** Estilo limpo esmaecido (fora do recorte ativo no modo limpo). */
const CLEAN_DIMMED_PATH: PathOptions = { ...CLEAN_PATH, opacity: 0.25 };

/**
 * Modo limpo (colorBy 'nenhum'): sem recorte = mapa cru; com qualquer recorte ativo
 * (regiao/partido/microrregiao/2º/eleitorado) os municipios que casam ganham realce e
 * os demais esmaecem. Partido isolado mantem a cor do partido (paridade).
 * diverge do golden por decisao de produto 2026-05-31.
 * fix 2026-06-01: regiao/recortes agora MARCAM no modo limpo (antes so o zoom acontecia,
 *   sem destaque). Reusa munDimmed p/ a decisao dentro/fora (DRY) — antes so olhava party.
 */
function cleanModeStyle(id: MunicipalityId, state: ExploreStyleState): PathOptions {
  const hasFilter = !!(state.party || state.region || state.micro || state.runnerUp || state.eleitoradoBand);
  if (!hasFilter) return CLEAN_PATH;
  if (munDimmed(id, state)) return CLEAN_DIMMED_PATH;
  if (state.party) return { fillColor: (state.g26 ? g26PartyColor(state.party) : PARTY_COLORS[state.party]) ?? FALLBACK_COLOR, fillOpacity: 0.55, color: '#FFFFFF', weight: 0.7, opacity: 0.85 };
  return CLEAN_SELECTED_PATH;
}

/** PathOptions de um municipio (cor + opacidades por dim) (assets/explore.js 287-290). */
export function munPathStyle(id: MunicipalityId, state: ExploreStyleState): PathOptions {
  if (state.colorBy === 'nenhum') return cleanModeStyle(id, state);
  const dim = munDimmed(id, state);
  return { fillColor: munColor(id, state), fillOpacity: dim ? 0.12 : 0.62, color: '#FFFFFF', weight: dim ? 0.4 : 0.7, opacity: dim ? 0.5 : 0.85 };
}

/** Cor neutra de zona para metricas sem dado por zona (close-up e so Manaus). */
const ZONE_NEUTRAL: HexColor = '#C9CCD1';

/** Cor de uma zona de Manaus pela metrica ativa (assets/explore.js 292-300). */
function zoneColor(key: ZonaKey, state: ExploreStyleState): HexColor {
  const metric = state.colorBy;
  if (metric === 'partido') return MANAUS_ELECTORAL_BY_YEAR[state.year]?.[key]?.[1] ?? '#ccc';
  if (metric === 'densidade') return MANAUS_LAYER_COLORS['heatmap-pct'][key] ?? '#9CC4DF';
  if (metric === 'vulnerabilidade' || metric === 'pertencimento') return MANAUS_LAYER_COLORS[metric][key] ?? '#ccc';
  const themeZone = ZONE_THEME[metric];
  if (themeZone) return themeZone[key] ?? '#ccc';
  // idh/populacao/competitividade/segundo/campanha/nenhum: sem dado por zona.
  // diverge do golden por decisao de produto 2026-05-31.
  return ZONE_NEUTRAL;
}

/** True quando a zona deve ser esmaecida pelos filtros (Manaus = Centro Amazonense). */
function zoneDimmed(key: ZonaKey, state: ExploreStyleState): boolean {
  if (state.region && state.region !== 'Centro Amazonense') return true;
  if (state.party) { const result = MANAUS_ELECTORAL_BY_YEAR[state.year]?.[key]; return !result || result[0] !== state.party; }
  return false;
}

/** PathOptions de uma zona (cor + opacidades por dim) (assets/explore.js 306-309). */
export function zonePathStyle(key: ZonaKey, state: ExploreStyleState): PathOptions {
  const dim = zoneDimmed(key, state);
  return { fillColor: zoneColor(key, state), fillOpacity: dim ? 0.1 : 0.58, color: '#FFFFFF', weight: dim ? 0.5 : 1, opacity: dim ? 0.5 : 0.9 };
}

/** Legenda vazia: esconde a caixa (renderLegend trata items.length === 0). */
const EMPTY_LEGEND: ExploreLegend = { title: '', items: [] };

/** Sufixo da legenda eleitoral do ano: cargo do pleito (modelo misto rotulado). */
function electoralSuffix(year: ElectoralYear): string {
  return ` · ${OFFICE_LABEL[ELECTION_OFFICE[year]]}`;
}

/** Monta o bloco de legenda da metrica ativa (assets/explore.js 378-394). */
export function buildExploreLegend(state: ExploreStyleState): ExploreLegend {
  const metric = state.colorBy;
  if (metric === 'nenhum') return EMPTY_LEGEND;
  if (state.g26 && G26_METRICS.has(metric)) return g26Legend(state.g26, metric);
  if (metric === 'partido') return presenceLegend(state.year, 'p', `Partido mais votado · ${state.year}${electoralSuffix(state.year)}`);
  if (metric === 'segundo') return presenceLegend(state.year, 's', `2º mais votado · ${state.year}${electoralSuffix(state.year)}`);
  if (metric === 'densidade') return bandedLegend(METRIC_META.densidade?.legendTitle ?? 'densidade', 'densidade', densBandLabels(state.densThresholds, 'mil'));
  if (metric === 'populacao') return bandedLegend(METRIC_META.populacao?.legendTitle ?? 'populacao', 'populacao', densBandLabels(state.popThresholds, 'mil hab'));
  if (metric === 'idh') return bandedLegend(METRIC_META.idh?.legendTitle ?? 'IDH', 'idh', IDH_BAND_LABELS);
  if (metric === 'competitividade') return bandedLegend(`${METRIC_META.competitividade?.legendTitle ?? 'Competitividade'}${electoralSuffix(state.year)}`, 'competitividade', COMP_BAND_LABELS);
  if (metric === 'alinhamento') return alignmentLegend();
  if (metric === 'cobertura') return bandedLegend(METRIC_META.cobertura?.legendTitle ?? 'Cobertura', 'cobertura', COBERTURA_BAND_LABELS);
  if (metric === 'reduto') return bandedLegend(METRIC_META.reduto?.legendTitle ?? 'Reduto', 'reduto', REDUTO_BAND_LABELS);
  if (metric === 'volatilidade') return bandedLegend(METRIC_META.volatilidade?.legendTitle ?? 'Volatilidade', 'volatilidade', VOLATILIDADE_BAND_LABELS);
  if (metric === 'variacao2422') return bandedLegend(METRIC_META.variacao2422?.legendTitle ?? 'Variação', 'variacao2422', VARIACAO_BAND_LABELS);
  if (metric === 'campoBatalha') return bandedLegend(METRIC_META.campoBatalha?.legendTitle ?? 'Campo de batalha', 'campoBatalha', CAMPO_BATALHA_BAND_LABELS);
  return thematicLegend(metric as ExploreThematicAxis);
}

/** Legenda do modo 2026: siglas presentes (lider/2º) ou faixas de competitividade. */
function g26Legend(sel: G26Selection, metric: ExploreColorMetric): ExploreLegend {
  const suffix = `2026 · ${G26_CARGO_SHORT[sel.cargo]} · 1º turno`;
  if (metric === 'competitividade') return bandedLegend(`${METRIC_META.competitividade?.legendTitle ?? 'Competitividade'} · ${suffix}`, 'competitividade', COMP_BAND_LABELS);
  const which = metric === 'segundo' ? 's' : 'p';
  const lead = sel.ds.cargos.find((c) => c.code === sel.cargo)?.proporcional ? 'Partido mais votado' : 'Mais votado';
  const title = which === 'p' ? `${lead} · ${suffix}` : `2º mais votado · ${suffix}`;
  return { title, items: g26PartiesPresent(sel, which).map((sigla) => [sigla, g26PartyColor(sigla)] as const) };
}

/** Legenda de alinhamento: 3 faixas com cor fixa (aliado/neutro/oposicao). */
function alignmentLegend(): ExploreLegend {
  const items = ALIGN_LEGEND.map(([label, value]) => [label, ALIGN_COLORS[value]] as const);
  return { title: METRIC_META.alinhamento?.legendTitle ?? 'Alinhamento', items };
}

/** Rotulos de cobertura/reduto (alinhados as rampas de 4 baldes). */
const COBERTURA_BAND_LABELS: readonly string[] = ['Sem cobertura', 'Baixa', 'Média', 'Alta'];
const REDUTO_BAND_LABELS: readonly string[] = ['Sem reduto', 'Fraco', 'Médio', 'Forte'];

/** Legenda de presenca partidaria (1º ou 2º): so siglas presentes, na ordem canonica. */
function presenceLegend(year: ElectoralYear, sigla: 'p' | 's', title: string): ExploreLegend {
  const present = new Set<string>();
  Object.values(EXPLORE_DATASET.munis).forEach((m) => {
    const result = m.el[year];
    if (result && PARTY_COLORS[result[sigla]]) present.add(result[sigla]);
  });
  const items = PARTY_ORDER.filter((p) => present.has(p)).map((p) => [p, PARTY_COLORS[p] as HexColor] as const);
  return { title, items };
}

/** Rotulos de 4 faixas por thresholds de quartil (densidade/populacao). */
function densBandLabels(thresholds: readonly [number, number, number], unit: string): readonly string[] {
  const [t0, t1, t2] = thresholds;
  return [`< ${t0} ${unit}`, `${t0}–${t1} ${unit}`, `${t1}–${t2} ${unit}`, `> ${t2} ${unit}`];
}

/** Faixas fixas de IDH (alinhadas a IDH_THRESHOLDS). */
const IDH_BAND_LABELS: readonly string[] = ['< 0,50', '0,50–0,55', '0,55–0,60', '> 0,60'];
/** Faixas fixas de competitividade (alinhadas a COMP_THRESHOLDS). */
const COMP_BAND_LABELS: readonly string[] = ['< 45% · disputado', '45–50%', '50–55%', '> 55% · dominante'];
/** Faixas dos índices estratégicos (decisao de produto 2026-06-01). */
const VOLATILIDADE_BAND_LABELS: readonly string[] = ['Fiel', 'Oscila', 'Instável', 'Imprevisível'];
const VARIACAO_BAND_LABELS: readonly string[] = ['Estável', 'Leve', 'Forte', 'Reviravolta'];
const CAMPO_BATALHA_BAND_LABELS: readonly string[] = ['Seguro', 'Observar', 'Disputado', 'Prioritário'];

/** Legenda de 4 baldes com a rampa nomeada e os rotulos dados. */
function bandedLegend(title: string, ramp: string, labels: readonly string[]): ExploreLegend {
  const colors = (RAMPS[ramp] ?? []) as readonly HexColor[];
  return { title, items: colors.map((c, i) => [labels[i] as string, c] as const) };
}

/** Legenda tematica: 4 baldes (Baixa..Muito alta) com a rampa do eixo. */
function thematicLegend(metric: ExploreThematicAxis): ExploreLegend {
  const ramp = (RAMPS[metric] ?? RAMPS.educacao ?? []) as readonly HexColor[];
  const title = METRIC_META[metric]?.legendTitle ?? metric;
  return { title, items: ramp.map((c, i) => [BUCKET_LABELS[i] as string, c] as const) };
}
